import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useFarm } from "@/context/FarmContext";
import { PageHeader, NoFarm } from "@/components/Page";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, Info, XCircle, ArrowRight, Users, Utensils } from "lucide-react";

const CFG = {
  danger: { icon: XCircle, cls: "border-l-destructive", chip: "bg-destructive/10 text-destructive", label: "Action needed" },
  warning: { icon: AlertTriangle, cls: "border-l-[hsl(43,74%,52%)]", chip: "bg-[hsl(43,74%,52%)]/15 text-[hsl(38,70%,35%)]", label: "Watch" },
  success: { icon: CheckCircle2, cls: "border-l-primary", chip: "bg-primary/10 text-primary", label: "On track" },
  info: { icon: Info, cls: "border-l-muted-foreground", chip: "bg-secondary text-muted-foreground", label: "Info" },
};

function AlertCard({ r, testid, navigate }) {
  const cfg = CFG[r.level] || CFG.info;
  const Icon = cfg.icon;
  const hasNums = r.requirement != null && r.supply != null;
  return (
    <Card className={`p-5 border-l-4 ${cfg.cls}`} data-testid={testid}>
      <div className="flex items-start gap-3">
        <Icon className="h-5 w-5 mt-0.5 shrink-0 text-[hsl(148,38%,16%)]" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-[hsl(148,38%,16%)]">{r.title}</h3>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.chip}`}>{cfg.label}</span>
            {r.nutrient && r.nutrient !== "Overall" && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{r.nutrient}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{r.message}</p>
          {hasNums && (
            <div className="flex flex-wrap gap-2 mt-3 text-xs" data-testid={`${testid}-nums`}>
              <span className="rounded-full bg-secondary px-2.5 py-1 tabular-nums">Requirement {r.requirement.toLocaleString()}{r.unit}</span>
              <span className="rounded-full bg-secondary px-2.5 py-1 tabular-nums">Supply {r.supply.toLocaleString()}{r.unit}</span>
              <span className={`rounded-full px-2.5 py-1 tabular-nums font-semibold ${r.delta < 0 ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                {r.delta > 0 ? "+" : ""}{r.delta.toLocaleString()}{r.unit} · {r.pct}%
              </span>
            </div>
          )}
          {r.cta && (
            <Button variant="outline" size="sm" className="rounded-full mt-3" data-testid={`${testid}-cta`} onClick={() => navigate(r.cta.path)}>
              {r.cta.label} <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function Recommendations() {
  const { farmId, loading } = useFarm();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["recs", farmId],
    queryFn: async () => (await api.get(`/farms/${farmId}/recommendations`)).data,
    enabled: !!farmId,
  });

  if (!loading && !farmId) return <NoFarm />;
  const farmRecs = data?.farm || [];
  const groups = data?.groups || [];

  return (
    <div>
      <PageHeader title="Recommendations" subtitle="Whole-farm grazing advice plus per-group nutrient balance from each group's active ration." />

      <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Farm-wide</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {farmRecs.map((r, i) => <AlertCard key={`farm-${i}`} r={r} testid={`recommendation-${i}`} navigate={navigate} />)}
        {farmRecs.length === 0 && <p className="text-muted-foreground text-sm">No farm-wide recommendations.</p>}
      </div>

      {groups.map((g, gi) => (
        <div key={g.group_id} className="mt-8" data-testid={`group-recs-${g.group_id}`}>
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">{g.group_name}</h2>
            <span className="text-xs text-muted-foreground">· {g.head_count} head · {g.group_type}</span>
            {g.ration_name && (
              <span className="inline-flex items-center gap-1 text-xs text-primary ml-auto"><Utensils className="h-3.5 w-3.5" /> {g.ration_name}</span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.alerts.map((r, i) => <AlertCard key={`${g.group_id}-${i}`} r={r} testid={`group-rec-${gi}-${i}`} navigate={navigate} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
